import React from 'react';
import _ from 'lodash';

import CoverSheet from '../general/coverSheet';
import ETAnomalyPDF from '../et/ETAnomalyPDF';
import PDFInfoSheet from '../../../themed/pdf/pdfInfoSheet';
import PDFPage from '../../../themed/pdf/pdfPage';
import PdfPhotoPage from '../../../themed/pdf/pdfPhotoPage';
import HelideckNetDropSummarySheet from './helideckNetSummarySheet';
import PdfTablePage from '../../../themed/pdf/pdfTablePage';

import withPDF from '../../../hoc/withPDF';

import * as dummyData from '../../../../utils/data';
import * as models from '../../../../utils/models';
import * as utils from '../../../../utils/utilities';

interface HelideckReportProps {
	anomalyData: models.VTEquipmentAnomalyData[];
	dtTableData: models.HelideckDropTestItem[];
	viTableData: models.HelideckVIItem[];
	sketchData: models.PDFImageProps[];
	coverSheetData: models.PDFCoverSheetProps;
	summarySheetData: models.HelideckNetDropSummary;
	visualReportData: models.PDFVIPageProps;
	drawingSheetPhotos: models.PDFImageProps[];
	photographicRecords: models.PDFImageProps[];
	projectDetails: models.Project;
}

const HelideckNetDropTestReport = (props: HelideckReportProps): JSX.Element => {
	const { summarySheetData } = props;

	const standardCriteriaItems: models.CoverDataItem[] = [
		{
			label: '1. Helideck Friction Requirement',
			value: props.coverSheetData?.helideckFrictionRequirement,
		},
		{
			label: '2. Test Tyre Specification',
			value: props.coverSheetData?.testTyreSpec,
		},
		{
			label: '3. Road Marking Specification',
			value: props.coverSheetData?.roadMarking,
		},
		{
			label: '4. Material On Calibration Friction Measurement',
			value: props.coverSheetData?.materialCalibration,
		},
		{
			label: '5. Calibration Friction Measurement',
			value: props.coverSheetData?.calibrationFriction,
		},
		{
			label: '6. International Organization For Standardization',
			value: props.coverSheetData?.iso,
		},
	];

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Drawing No',
			value: props.coverSheetData?.drawingNo,
		},
		{
			label: '2. Visual Testing Procedure',
			value: props.coverSheetData?.visualTesting,
		},
		{
			label: '3. Helideck Co-efficient of Friction Test Preocedure.',
			value: props.coverSheetData?.helideckCoefficient,
		},
	];

	const getPhotographicRecord = (): models.PDFImageProps[][] => {
		const images: models.PDFImageProps[] = [...props.photographicRecords];
		const viInspectionImages = props.viTableData
			.map((item) => item.images)
			.flat()
			.concat(images);

		return _.chunk(viInspectionImages, 4);
	};

	const getSummaryData = (): models.HelideckSummaryItem[] => {
		return props.photographicRecords.map((record) => ({
			description: record.caption,
			photoRef: 'Yes',
		}));
	};

	const getTestResult = (): string => {
		if (!summarySheetData) return '';
		return summarySheetData?.numberOfNetFailed > 0 ? 'FAIL' : 'PASS';
	};

	return (
		<React.Fragment>
			<PDFInfoSheet
				companyUrl={props.projectDetails.organisation.organisationLogo}
				documentNo={props.projectDetails.documentNumber}
				clientName={props.projectDetails.organisationName}
				facility={props.projectDetails.facilityName}
				reportTitle={props.projectDetails.projectTitle}
			/>

			<PDFPage
				pageDetails={utils.getPageDetails('Cover Sheet')}
				projectDetails={props.projectDetails}
			>
				<CoverSheet
					overviewData={overviewData}
					referenceDocuments={referenceDocuments}
					standardCriteriaItems={standardCriteriaItems}
					note="Refer to work instruction for inspection requirement"
					testResult={getTestResult()}
				/>
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Summary Sheet')}
				projectDetails={props.projectDetails}
			>
				<HelideckNetDropSummarySheet
					weightLabel="Dummy Shell Weight (Kg)"
					totalLabel="Total Number of Net"
					passedLabel="Total Number of Net Passed"
					failedLabel="Total Number of Net Failed"
					summaryData={getSummaryData()}
					{...summarySheetData}
				/>
			</PDFPage>

			{utils.getChunkedData(props.viTableData, 15).map((data, i: number) => (
				<PDFPage
					key={data[i].id.toString()}
					pageDetails={utils.getPageDetails('Visual Inspection Report')}
					projectDetails={props.projectDetails}
					enableTitlePage={i === 0}
				>
					<PdfTablePage
						data={data}
						columns={dummyData.getHelideckVIColumns()}
					/>
				</PDFPage>
			))}

			<PDFPage
				pageDetails={utils.getPageDetails('Drawing Sheet')}
				projectDetails={props.projectDetails}
			>
				<PdfPhotoPage photos={props.drawingSheetPhotos} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Inspection Sketch')}
				projectDetails={props.projectDetails}
			>
				<PdfPhotoPage photos={props.sketchData} />
			</PDFPage>

			{utils.getChunkedData(props.dtTableData, 15).map((data, i: number) => (
				<PDFPage
					key={data[i].id.toString()}
					pageDetails={utils.getPageDetails('Net Drop Test Result')}
					projectDetails={props.projectDetails}
					enableTitlePage={i === 0}
				>
					<PdfTablePage
						data={data}
						columns={dummyData.getHelideckDTColumns()}
					/>
				</PDFPage>
			))}

			<ETAnomalyPDF data={props.anomalyData} project={props.projectDetails} />

			{getPhotographicRecord().map((photos, index: number) => (
				<PDFPage
					key={photos[index].src}
					enableTitlePage={index === 0}
					pageDetails={utils.getPageDetails('Photographic Record')}
					projectDetails={props.projectDetails}
				>
					<PdfPhotoPage photos={photos} />
				</PDFPage>
			))}
		</React.Fragment>
	);
};

const overviewData: models.CoverDataOverview[] = [
	{ report: '1. Cover Sheet', required: 'Yes', page: '2' },
	{ report: '2. Summary Sheet', required: 'Yes', page: '3' },
	{ report: '3. Visual Inspection Report', required: 'Yes', page: '5' },
	{
		report: '4. Drawing Sheet',
		required: 'Yes',
		page: '6',
	},
	{
		report: '5. Inspection Sketch',
		required: 'Yes',
		page: '7',
	},
	{ report: '6. Test Result', required: 'Yes', page: '8' },
	{ report: '7. Anomaly Data', required: 'Yes', page: '10' },
	{ report: '8. Photographic Record', required: 'Yes', page: '12' },
];

export default withPDF(HelideckNetDropTestReport);
